import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Typography } from '@mui/material';
import moment from 'moment';

import { ClientInvitationSearchFilter, ClientInvitationStatus } from 'api';
import { CheckedMode } from 'types/app';
import { EmptyState } from 'components';
import { useCheckedState, useDateRange, useNotify } from 'hooks';
import { useSearchClientInvitations, useSearchInvitationsReport } from 'features/admin/hooks';
import { useDeleteClientInvitationsNext } from 'features/admin/updaters';
import { PAGINATION_LIMIT } from 'features/admin/constants';

import {
  AcceptedTable,
  Actions,
  FilterBar,
  Header,
  PendingTable,
  TableHeader,
} from '../components/Invitations';

const Invitations: FC = () => {
  const { t } = useTranslation();
  const dateRangeOptions = useDateRange({ includeToday: true });
  const checkedOptions = useCheckedState();
  const notify = useNotify();

  const { checkedIds, checkedMode, resetCheckedState } = checkedOptions;
  const { startDate, endDate } = dateRangeOptions;

  const [searchFilter, setSearchFilter] = useState<ClientInvitationSearchFilter>({
    query: '',
    invitation_status__eq: ClientInvitationStatus.Active,
    created_at__gte: moment(startDate).unix(),
    created_at__lt: moment(endDate).add(1, 'days').unix(),
    department_id__in: [],
    location_id__in: [],
    added_by_user_id__in: [],
  });

  const queryVariables = {
    search_filter: searchFilter,
    limit: PAGINATION_LIMIT,
    count: true,
    report: false,
  };

  const queryOptions = useSearchClientInvitations(queryVariables);
  const deleteMutationOptions = useDeleteClientInvitationsNext(queryVariables);

  const { isEmpty, results, count } = queryOptions;
  const isPending = searchFilter.invitation_status__eq === ClientInvitationStatus.Active;
  const isAccepted = searchFilter.invitation_status__eq === ClientInvitationStatus.Accepted;
  const actionCount = checkedIds.length || count;

  const [requestReport, setRequestReport] = useState(false);

  const reportVariables = {
    search_filter: searchFilter,
    report: true,
  };

  useSearchInvitationsReport(reportVariables, {
    enabled: requestReport,
    onError: () => {
      notify.error({ message: t('analytics:reportRequestedError') });
    },
    onSettled: () => {
      setRequestReport(false);
    },
  });

  function handleSetFilter(nextFilter: ClientInvitationSearchFilter) {
    resetCheckedState();
    setSearchFilter(nextFilter);
  }

  function handleResetFilters() {
    setSearchFilter({
      ...searchFilter,
      department_id__in: [],
      location_id__in: [],
      added_by_user_id__in: [],
    });
  }

  function onRequestReport() {
    notify.info({ message: t('analytics:reportRequested') });
    setRequestReport(true);
  }

  function renderTable() {
    if (isEmpty) {
      return (
        <EmptyState
          name="invitations"
          actionComponent={
            <Button color="primary" onClick={handleResetFilters}>
              <Typography variant="subtitle2" align="center">
                {t('admin:clearFilters')}
              </Typography>
            </Button>
          }
        />
      );
    }

    if (isPending) {
      return (
        <PendingTable
          deleteMutationOptions={deleteMutationOptions}
          {...checkedOptions}
          {...queryOptions}
        />
      );
    }

    if (isAccepted) {
      return <AcceptedTable {...queryOptions} />;
    }

    return null;
  }

  function getActionVariables() {
    if (checkedMode === CheckedMode.ALL) {
      return { search_filter: searchFilter };
    }

    if (checkedMode === CheckedMode.PAGE) {
      return { invitation_ids: results.map(result => result.id) };
    }

    return { invitation_ids: checkedIds };
  }

  return (
    <Fragment>
      <Box pb={2}>
        <Header onRequestReport={onRequestReport} />
      </Box>
      <Box pb={3}>
        <FilterBar
          searchFilter={searchFilter}
          onSetSearchFilter={handleSetFilter}
          {...dateRangeOptions}
        />
      </Box>
      <Paper>
        <TableHeader
          searchFilter={searchFilter}
          onSetSearchFilter={handleSetFilter}
          {...checkedOptions}
          actions={
            <Actions
              bulk
              count={actionCount}
              variables={getActionVariables()}
              deleteMutationOptions={deleteMutationOptions}
              {...checkedOptions}
            />
          }
        />
        {renderTable()}
      </Paper>
    </Fragment>
  );
};

export default Invitations;
